import { useCallback, useMemo } from 'react';
import { useUndoRedoStore } from '@/stores/undoRedoStore';
import { annotationService } from '@/services/annotationService';
import { useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { logger } from '@/lib/logger';
import { annotationQueryKey } from '@/hooks/useAnnotations';
import { queryKeys } from '@/lib/queryKeys';
import { fixtureServiceAdapter } from '@/services/adapters/fixtureServiceAdapter';
import { controlServiceAdapter } from '@/services/adapters/controlServiceAdapter';
import { roomServiceAdapter } from '@/services/adapters/roomServiceAdapter';
import { cableJunctionServiceAdapter } from '@/services/adapters/cableJunctionServiceAdapter';
import { cablePathServiceAdapter } from '@/services/adapters/cablePathServiceAdapter';
import { lvProductService } from '@/features/product-library/services/lvProductService';

export function useAnnotationUndoRedo(floorPlanId: string | undefined) {
  const queryClient = useQueryClient();
  const { undo, redo, canUndo, canRedo, getUndoDescription, getRedoDescription } =
    useUndoRedoStore();

  const productQueryKey = useMemo(
    () => (floorPlanId ? ([...queryKeys.floorPlan(floorPlanId), 'lv-products'] as const) : null),
    [floorPlanId],
  );

  const replaceCachedItem = useCallback(
    (queryKey: readonly unknown[], item: any) => {
      queryClient.setQueryData<any[]>(queryKey, (old = []) =>
        Array.isArray(old)
          ? old.map(existing => (existing.id === item.id ? { ...existing, ...item } : existing))
          : old,
      );
    },
    [queryClient],
  );

  const invalidateChangedEntity = useCallback(
    (entityType: string) => {
      if (!floorPlanId) return;

      const keyByType: Record<string, readonly unknown[]> = {
        fixture: queryKeys.fixtures(floorPlanId),
        control: queryKeys.controls(floorPlanId),
        room: queryKeys.rooms(floorPlanId),
        product: productQueryKey ?? [],
        cableJunction: queryKeys.entitiesByFloorPlan('cable_junctions', floorPlanId),
        cablePath: queryKeys.entitiesByFloorPlan('cable_paths', floorPlanId),
      };

      const queryKey = keyByType[entityType];
      if (queryKey?.length) {
        queryClient.invalidateQueries({ queryKey });
      }
    },
    [floorPlanId, productQueryKey, queryClient],
  );

  const applyEntityUpdate = useCallback(
    async (entityType: string, id: string, updates: any) => {
      if (!floorPlanId) return;

      switch (entityType) {
        case 'fixture': {
          const updated = await fixtureServiceAdapter.update(id, updates);
          replaceCachedItem(queryKeys.fixtures(floorPlanId), updated);
          break;
        }
        case 'control': {
          const updated = await controlServiceAdapter.update(id, updates);
          replaceCachedItem(queryKeys.controls(floorPlanId), updated);
          break;
        }
        case 'product': {
          const updated = await lvProductService.update(id, updates);
          if (productQueryKey) {
            replaceCachedItem(productQueryKey, updated);
          }
          break;
        }
        case 'room': {
          const updated = await roomServiceAdapter.update(id, updates);
          replaceCachedItem(queryKeys.rooms(floorPlanId), updated);
          break;
        }
        case 'cableJunction': {
          const updated = await cableJunctionServiceAdapter.update(id, updates);
          replaceCachedItem(queryKeys.entitiesByFloorPlan('cable_junctions', floorPlanId), updated);
          break;
        }
        case 'cablePath': {
          const updated = await cablePathServiceAdapter.update(id, updates);
          replaceCachedItem(queryKeys.entitiesByFloorPlan('cable_paths', floorPlanId), updated);
          break;
        }
        default:
          logger.warn('Unsupported undo entity update:', entityType);
          return;
      }

      invalidateChangedEntity(entityType);
    },
    [floorPlanId, invalidateChangedEntity, productQueryKey, replaceCachedItem],
  );

  const applyStoredAction = useCallback(
    async (action: any, direction: 'undo' | 'redo') => {
      const isUndo = direction === 'undo';

      switch (action.type) {
        case 'UPDATE_PRODUCT':
          await applyEntityUpdate(
            'product',
            action.data.id,
            isUndo ? action.data.oldData : action.data.newData,
          );
          break;

        case 'UPDATE_FIXTURE':
          await applyEntityUpdate(
            'fixture',
            action.data.id,
            isUndo ? action.data.oldData : action.data.newData,
          );
          break;

        case 'UPDATE_CONTROL':
          await applyEntityUpdate(
            'control',
            action.data.id,
            isUndo ? action.data.oldData : action.data.newData,
          );
          break;

        case 'UPDATE_ROOM':
          await applyEntityUpdate(
            'room',
            action.data.id,
            isUndo ? action.data.oldData : action.data.newData,
          );
          break;

        case 'UPDATE_CABLE_JUNCTION':
          await applyEntityUpdate(
            'cableJunction',
            action.data.id,
            isUndo ? action.data.oldData : action.data.newData,
          );
          break;

        case 'UPDATE_CABLE_PATH':
          await applyEntityUpdate(
            'cablePath',
            action.data.id,
            isUndo ? action.data.oldData : action.data.newData,
          );
          break;

        case 'MOVE_ENTITY': {
          const position = isUndo ? action.data.oldPosition : action.data.newPosition;
          const positionUpdates =
            action.data.entityType === 'product'
              ? { x_position: position.x, y_position: position.y }
              : { x: position.x, y: position.y };
          await applyEntityUpdate(action.data.entityType, action.data.id, positionUpdates);
          break;
        }

        case 'ROTATE_ENTITY': {
          const rotation = isUndo ? action.data.oldRotation : action.data.newRotation;
          await applyEntityUpdate(action.data.entityType, action.data.id, { rotation });
          break;
        }

        case 'RESIZE_ENTITY': {
          const scale = isUndo ? action.data.oldScale : action.data.newScale;
          await applyEntityUpdate(action.data.entityType, action.data.id, { scale });
          break;
        }

        case 'BATCH_RESIZE_ENTITY': {
          const ids: string[] = action.data.productIds || [];
          await Promise.all(
            ids.map(id => {
              const scale = isUndo ? action.data.oldScales[id] : action.data.newScale;
              return applyEntityUpdate(action.data.entityType, id, { scale });
            }),
          );
          break;
        }

        default:
          return false;
      }

      return true;
    },
    [applyEntityUpdate],
  );

  const performUndo = useCallback(async () => {
    if (!floorPlanId) return;

    const action = undo();
    if (!action) return;

    try {
      switch (action.type) {
        case 'CREATE_ANNOTATION':
          // To undo create, we delete
          await annotationService.deleteAnnotation(action.data.annotation.id);
          toast.success('Undone: Create annotation');
          break;

        case 'UPDATE_ANNOTATION':
          // To undo update, we restore old data
          await annotationService.updateAnnotation(action.data.id, action.data.oldData);
          toast.success('Undone: Update annotation');
          break;

        case 'DELETE_ANNOTATION': {
          // To undo delete, we recreate
          const { id, ...createData } = action.data.annotation;
          const newAnnotation = await annotationService.createAnnotation(createData);

          // Update the action data with the new ID for potential redo
          action.data.annotation.id = newAnnotation.id;
          toast.success('Undone: Delete annotation');
          break;
        }

        case 'BATCH_DELETE_ANNOTATIONS':
          // To undo batch delete, we recreate all
          for (const annotation of action.data.annotations) {
            const { id, ...createData } = annotation;
            await annotationService.createAnnotation(createData);
          }
          toast.success(`Undone: Delete ${action.data.annotations.length} annotations`);
          break;

        case 'PASTE_ANNOTATIONS':
          // To undo paste, we delete the pasted annotations
          for (const annotation of action.data.annotations) {
            await annotationService.deleteAnnotation(annotation.id);
          }
          toast.success(`Undone: Paste ${action.data.annotations.length} annotations`);
          break;

        default: {
          const handled = await applyStoredAction(action, 'undo');
          if (!handled) {
            logger.warn('Undo action is not implemented:', action.type);
          }
          break;
        }
      }

      // Refresh the annotations
      queryClient.invalidateQueries({ queryKey: annotationQueryKey(floorPlanId, 'floor_plan') });
    } catch (error) {
      logger.error('Failed to undo action:', error);
      toast.error('Failed to undo action');

      // Put the action back in history since it failed
      redo();
    }
  }, [floorPlanId, undo, redo, queryClient, applyStoredAction]);

  const performRedo = useCallback(async () => {
    if (!floorPlanId) return;

    const action = redo();
    if (!action) return;

    try {
      switch (action.type) {
        case 'CREATE_ANNOTATION': {
          // To redo create, we create again
          const { id, ...createData } = action.data.annotation;
          const newAnnotation = await annotationService.createAnnotation(createData);

          // Update the action data with the new ID
          action.data.annotation.id = newAnnotation.id;
          toast.success('Redone: Create annotation');
          break;
        }

        case 'UPDATE_ANNOTATION':
          // To redo update, we apply new data
          await annotationService.updateAnnotation(action.data.id, action.data.newData);
          toast.success('Redone: Update annotation');
          break;

        case 'DELETE_ANNOTATION':
          // To redo delete, we delete again
          await annotationService.deleteAnnotation(action.data.annotation.id);
          toast.success('Redone: Delete annotation');
          break;

        case 'BATCH_DELETE_ANNOTATIONS':
          // To redo batch delete, we delete all again
          for (const annotation of action.data.annotations) {
            await annotationService.deleteAnnotation(annotation.id);
          }
          toast.success(`Redone: Delete ${action.data.annotations.length} annotations`);
          break;

        case 'PASTE_ANNOTATIONS': {
          // To redo paste, we create the annotations again
          const newAnnotations = [];
          for (const annotation of action.data.annotations) {
            const { id, ...createData } = annotation;
            const newAnnotation = await annotationService.createAnnotation(createData);
            newAnnotations.push(newAnnotation);
          }
          // Update the action data with new IDs
          action.data.annotations = newAnnotations;
          toast.success(`Redone: Paste ${action.data.annotations.length} annotations`);
          break;
        }

        default: {
          const handled = await applyStoredAction(action, 'redo');
          if (!handled) {
            logger.warn('Redo action is not implemented:', action.type);
          }
          break;
        }
      }

      // Refresh the annotations
      queryClient.invalidateQueries({ queryKey: annotationQueryKey(floorPlanId, 'floor_plan') });
    } catch (error) {
      logger.error('Failed to redo action:', error);
      toast.error('Failed to redo action');

      // Put the action back in history since it failed
      undo();
    }
  }, [floorPlanId, undo, redo, queryClient, applyStoredAction]);

  return {
    performUndo,
    performRedo,
    canUndo: canUndo(),
    canRedo: canRedo(),
    undoDescription: getUndoDescription(),
    redoDescription: getRedoDescription(),
  };
}
